import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../App';
import { authAPI, seedAPI } from '../services/api';
import api from '../services/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';
import { Building2, Loader2 } from 'lucide-react';

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [seeding, setSeeding] = useState(false);
  const [seedStatus, setSeedStatus] = useState({ loading: true, seeded: true });
  const { login } = useAuth();
  const navigate = useNavigate();

  // Hide the destructive 'Initialize Demo Data' button whenever the database
  // already has users. Prevents accidental production data wipes.
  useEffect(() => {
    let active = true;
    api.get('/seed/status')
      .then((r) => { if (active) setSeedStatus({ loading: false, seeded: !!r.data?.seeded }); })
      .catch(() => { if (active) setSeedStatus({ loading: false, seeded: true }); });
    return () => { active = false; };
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await authAPI.login(email, password);
      const { access_token, user } = response.data;
      login(user, access_token);
      toast.success('Welcome back!');
      navigate('/');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid credentials');
    } finally {
      setLoading(false);
    }
  };

  const handleSeedDatabase = async () => {
    setSeeding(true);
    try {
      const response = await seedAPI.seed();
      toast.success('Database seeded! Use admin@prideglobal.com / admin123');
      setEmail('admin@prideglobal.com');
      setPassword('admin123');
    } catch (error) {
      toast.error('Failed to seed database');
    } finally {
      setSeeding(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* Left side - Image */}
      <div 
        className="hidden lg:flex lg:w-1/2 relative bg-cover bg-center"
        style={{
          backgroundImage: `url('https://images.unsplash.com/photo-1760124056883-732e7a5e2e68?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzF8MHwxfHNlYXJjaHwzfHxtb2Rlcm4lMjBjb3Jwb3JhdGUlMjBvZmZpY2UlMjBidWlsZGluZ3xlbnwwfHx8fDE3NzQ0NDk4MzN8MA&ixlib=rb-4.1.0&q=85')`
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-r from-primary/80 to-primary/40" />
        <div className="relative z-10 flex flex-col justify-end p-12 text-white">
          <h1 className="text-4xl font-bold mb-4">Pride Global HRMS</h1>
          <p className="text-lg opacity-90">
            Enterprise-grade Human Resource Management System for global operations across 9 countries.
          </p>
          <div className="flex flex-wrap gap-2 mt-6">
            {['UAE', 'India', 'UK', 'Singapore', 'Ghana', 'Kenya', 'Uganda', 'Cameroon', 'Mauritius'].map((country) => (
              <span key={country} className="px-3 py-1 bg-white/20 rounded-full text-sm">
                {country}
              </span>
            ))}
          </div>
        </div>
      </div>

      {/* Right side - Login form */}
      <div className="flex-1 flex items-center justify-center p-6 bg-background">
        <div className="w-full max-w-md">
          {/* Mobile logo */}
          <div className="lg:hidden flex items-center gap-3 mb-8 justify-center">
            <div className="w-12 h-12 rounded-xl bg-primary flex items-center justify-center">
              <Building2 className="text-primary-foreground" size={24} />
            </div>
            <span className="text-2xl font-bold">Pride Global</span>
          </div>

          <Card className="border-0 shadow-lg">
            <CardHeader className="space-y-1 pb-6">
              <CardTitle className="text-2xl font-bold">Welcome back</CardTitle>
              <CardDescription>
                Enter your credentials to access your account
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="admin@prideglobal.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    data-testid="login-email"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="password">Password</Label>
                  <Input
                    id="password"
                    type="password"
                    placeholder="Enter your password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    data-testid="login-password"
                  />
                </div>
                <Button 
                  type="submit" 
                  className="w-full btn-hover"
                  disabled={loading}
                  data-testid="login-submit"
                >
                  {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  Sign In
                </Button>
              </form>

              {!seedStatus.loading && !seedStatus.seeded && (
                <div className="mt-6 pt-6 border-t">
                  <p className="text-sm text-muted-foreground text-center mb-4">
                    First time? Seed the database with sample data
                  </p>
                  <Button
                    variant="outline"
                    className="w-full"
                    onClick={handleSeedDatabase}
                    disabled={seeding}
                    data-testid="seed-database-btn"
                  >
                    {seeding && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                    Initialize Demo Data
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>

          <p className="text-center text-sm text-muted-foreground mt-6">
            Protected by enterprise-grade security
          </p>
        </div>
      </div>
    </div>
  );
};

export default Login;
